import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2, Play, Copy, ScanText, FileInput } from "lucide-react";
import { Field, TextInput, PrimaryButton, GhostButton, FileDrop, ShareButton } from "@/tools/ui";
import { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import axios from "axios";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const ProgressBar = ({ phase, progress }) => (
  <div data-testid="job-progress" className="space-y-1">
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
    </div>
    <p className="text-right text-xs text-muted-foreground">{phase} {progress}%</p>
  </div>
);

export const PdfFillForm = () => {
  const [file, setFile] = useState(null);
  const [fields, setFields] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  const onFile = async (f) => {
    setFile(f); setFields(null); setVals({});
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await axios.post(`${API}/pdf/form-fields`, fd, { timeout: 60000 });
      const list = res.data.fields || [];
      setFields(list);
      setVals(Object.fromEntries(list.map((x) => [x.name, x.value || ""])));
      if (list.length === 0) toast.error("This PDF has no fillable form fields.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the PDF form.");
    } finally { setBusy(false); }
  };

  const run = async () => {
    if (!fields || fields.length === 0) return;
    setBusy(true);
    try {
      const values = Object.entries(vals).map(([k, v]) => `${k}:${v}`).join("|");
      const fd = new FormData(); fd.append("file", file); fd.append("values", values);
      const res = await axios.post(`${API}/pdf/fill-form`, fd, { responseType: "blob", timeout: 120000 });
      downloadBlob(res.data, "filled.pdf");
      toast.success("Form filled — file downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fill the form.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="fillform-drop" accept="application/pdf" label="Drop a fillable PDF or click to upload" onFiles={(f) => onFile(f[0])} />
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name}</span>
          <GhostButton data-testid="fillform-reset" onClick={() => { setFile(null); setFields(null); setVals({}); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}

      {fields && fields.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <Field key={f.name} label={f.name}>
              <TextInput data-testid={`fillform-field-${f.name}`} value={vals[f.name] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))} />
            </Field>
          ))}
        </div>
      )}

      {fields && fields.length === 0 && (
        <p className="rounded-lg border border-dashed border-border bg-muted p-4 text-sm text-muted-foreground">
          <FileInput className="mr-2 inline h-4 w-4" /> This PDF does not contain any interactive form fields, so there is nothing to fill.
        </p>
      )}

      {fields && fields.length > 0 && (
        <PrimaryButton data-testid="fillform-run" onClick={run} disabled={busy}>
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</> : <><Play className="h-4 w-4" /> Fill & Download</>}
        </PrimaryButton>
      )}
    </div>
  );
};

export const PdfOcr = () => {
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");

  const run = async () => {
    if (!file) return toast.error("Upload a scanned PDF first");
    setBusy(true); setText(""); setProgress(0); setPhase("Uploading");
    try {
      const fd = new FormData(); fd.append("file", file);
      const sub = await axios.post(`${API}/jobs/ocr-pdf`, fd, {
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 30)),
      });
      const jid = sub.data.job_id;
      setPhase("Recognising text");
      for (let i = 0; i < 200; i++) {
        const s = await axios.get(`${API}/jobs/${jid}`);
        setProgress(30 + Math.round((s.data.progress || 0) * 0.7));
        if (s.data.status === "done") break;
        if (s.data.status === "error") throw new Error(s.data.error || "OCR failed");
        await sleep(1200);
      }
      const res = await axios.get(`${API}/jobs/${jid}/result`, { responseType: "text" });
      setText(res.data);
      toast.success("Text extracted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "OCR failed.");
    } finally { setBusy(false); setProgress(0); setPhase(""); }
  };

  return (
    <div className="space-y-6">
      {!file ? (
        <FileDrop testid="pdfocr-drop" accept="application/pdf" label="Drop a scanned PDF or click to upload" onFiles={(f) => { setFile(f[0]); setText(""); }} />
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{file.name}</span>
          <GhostButton data-testid="pdfocr-reset" onClick={() => { setFile(null); setText(""); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}
      <PrimaryButton data-testid="pdfocr-run" onClick={run} disabled={busy || !file}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {phase || "Working"}…</> : <><ScanText className="h-4 w-4" /> Extract Text (OCR)</>}
      </PrimaryButton>
      {busy && progress > 0 && <ProgressBar phase={phase} progress={progress} />}
      {text && (
        <div className="rounded-xl border border-border bg-muted">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="overline text-muted-foreground">Extracted Text</span>
            <div className="flex gap-2">
              <GhostButton data-testid="pdfocr-copy" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
              <ShareButton testid="pdfocr-share" text={text} />
            </div>
          </div>
          <pre data-testid="pdfocr-output" className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-4 text-sm">{text}</pre>
        </div>
      )}
    </div>
  );
};
